import { api } from './api';
import type { Person } from './session';

/** mcper.io's own numbers, shown to its admin only. */
export type Growth = { library: number; building: number; people: number; active7d: number; calls7d: number };

/** What the sign-in page needs: the ways to sign in. */
export type McperProviders = { providers: string[]; localLoginEnabled: boolean };

/** A signed-in session: the access token and the person. */
export type Session = { accessToken: string; person: Person };

export type SiteStatus = 'building' | 'ready' | 'unbuildable' | 'failed';

/** A site as the server summarises it (docs/api.md, /api/mcper). */
export type SiteSummary = {
  site: string;
  name: string;
  status: SiteStatus;
  tools: number;
  kind: 'API' | 'Replay' | 'API + Replay' | '—';
  signIn: 'none' | 'token';
  stage?: string;
  progress: number;
  reason?: string;
  updatedAt: string;
};

export type MySite = SiteSummary & { lastUsedAt: string; uses: number; builtByYou: boolean };

/** A person's sign-in to a site; the token itself never comes back. */
export type SiteSignIn = { site: string; name: string; state: 'connected' | 'expired' | 'revoked' | 'never'; kind: 'token'; connectedAt: string; lastUsedAt?: string; expiresAt?: string };

/** What the sign-in page needs about one site. */
export type SiteSignInInfo = { site: string; name: string; status: SiteStatus; signIn: 'none' | 'token'; signedIn: boolean };

export type Agent = { id: string; name: string; connectedAt: string; lastUsedAt?: string; calls: number };
export type CallRow = { id: string; at: string; site: string; tool: string; kind: 'read' | 'preview' | 'write' | 'refused'; ok: boolean; code?: string; message?: string; durationMs?: number; tier?: string };
export type Stats = { calls7d: number; writes7d: number; typicalMs?: number; apiMs?: number; replayMs?: number };
export type Plan = { plan: 'free'; month: string; calls: number; writes: number; cap: number; resetsAt: string };

/** The consent page: which agent is asking, and whether it may be let in. */
export type ConsentRequest = { agent: string; allowed: boolean; reason?: string };

/** The person's view of mcper.io. */
export type McperOverview = {
  mcpUrl: string;
  sites: MySite[];
  library: SiteSummary[];
  building: SiteSummary[];
  counts: { library: number; building: number };
  signIns: SiteSignIn[];
  agents: Agent[];
  calls: CallRow[];
  stats: Stats;
  plan: Plan;
};

/** The address agents use: this host's /mcp (it answers on every mcper.io host). */
export function mcpUrl(): string {
  return `${window.location.origin}/mcp`;
}

export const mcperService = {
  providers(): Promise<McperProviders> {
    return api.get<McperProviders>('/auth/providers');
  },
  localLogin(email: string): Promise<Session> {
    return api.post<Session>('/auth/local/login', { email });
  },
  /** Full-page redirect to the server, which goes to the provider and back to `redirect` (a path of this app). */
  startSso(redirect?: string, provider?: string): void {
    const q = new URLSearchParams();
    if (provider) q.set('provider', provider);
    if (redirect) q.set('redirect', redirect);
    const qs = q.toString();
    window.location.assign(qs ? `/auth/login?${qs}` : '/auth/login');
  },
  refresh(): Promise<Session> {
    return api.post<Session>('/auth/refresh');
  },
  me(): Promise<Person> {
    return api.get<Person>('/api/mcper/me');
  },
  logout(): Promise<void> {
    return api.post<void>('/auth/logout');
  },
  mySites(): Promise<MySite[]> {
    return api.get<MySite[]>('/api/mcper/sites');
  },
  library(q?: string): Promise<SiteSummary[]> {
    return api.get<SiteSummary[]>(q ? `/api/mcper/library?q=${encodeURIComponent(q)}` : '/api/mcper/library');
  },
  building(): Promise<SiteSummary[]> {
    return api.get<SiteSummary[]>('/api/mcper/building');
  },
  /** mcper.io's growth; null unless the person is its admin. */
  growth(): Promise<Growth | null> {
    return api.get<Growth | null>('/api/mcper/growth');
  },
  counts(): Promise<{ library: number; building: number }> {
    return api.get<{ library: number; building: number }>('/api/mcper/counts');
  },
  site(site: string): Promise<SiteSignInInfo> {
    return api.get<SiteSignInInfo>(`/api/mcper/sites/${encodeURIComponent(site)}`);
  },
  saveToken(site: string, token: string): Promise<{ site: string }> {
    return api.put<{ site: string }>(`/api/mcper/sites/${encodeURIComponent(site)}/token`, { token });
  },
  signIns(): Promise<SiteSignIn[]> {
    return api.get<SiteSignIn[]>('/api/mcper/sign-ins');
  },
  revokeSignIn(site: string): Promise<void> {
    return api.delete<void>(`/api/mcper/sign-ins/${encodeURIComponent(site)}`);
  },
  agents(): Promise<Agent[]> {
    return api.get<Agent[]>('/api/mcper/agents');
  },
  revokeAgent(id: string): Promise<void> {
    return api.delete<void>(`/api/mcper/agents/${encodeURIComponent(id)}`);
  },
  calls(limit = 100): Promise<CallRow[]> {
    return api.get<CallRow[]>(`/api/mcper/calls?limit=${limit}`);
  },
  stats(): Promise<Stats> {
    return api.get<Stats>('/api/mcper/stats');
  },
  plan(): Promise<Plan> {
    return api.get<Plan>('/api/mcper/plan');
  },
  deleteAccount(): Promise<void> {
    return api.delete<void>('/api/mcper/account');
  },
  consent(req: string): Promise<ConsentRequest> {
    return api.get<ConsentRequest>(`/api/mcper/consent/${encodeURIComponent(req)}`);
  },
  allow(req: string): Promise<{ redirectTo: string }> {
    return api.post<{ redirectTo: string }>(`/api/mcper/consent/${encodeURIComponent(req)}/allow`);
  },
  deny(req: string): Promise<{ redirectTo: string }> {
    return api.post<{ redirectTo: string }>(`/api/mcper/consent/${encodeURIComponent(req)}/deny`);
  },
  /** Which agent is waiting, before sign-in (its name only). */
  waiting(req: string): Promise<{ agent: string } | null> {
    return api.get<{ agent: string } | null>(`/auth/waiting/${encodeURIComponent(req)}`);
  },
  async overview(): Promise<McperOverview> {
    const [sites, library, building, counts, signIns, agents, calls, stats, plan] = await Promise.all([
      this.mySites(),
      this.library(),
      this.building(),
      this.counts(),
      this.signIns(),
      this.agents(),
      this.calls(5),
      this.stats(),
      this.plan(),
    ]);
    return { mcpUrl: mcpUrl(), sites, library, building, counts, signIns, agents, calls, stats, plan };
  },
};
